import { useCallback, useEffect, useState } from 'react'
import {
  AdminsApi,
  AdminsLoyaltyGetVoucherListResponseItem,
  Configuration,
  PaymentMethodEnum,
  UserRoleEnum,
  VoucherTypeEnum,
} from 'generated/api'
import { IFilter } from 'constants/tags'
import { axiosDefaultConfig } from 'constants/axios'
import Toast from 'components/toast/Toast'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)

export enum VoucherStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const useVoucher = () => {
  const { toast, toastContainer } = Toast()
  const [fetchVouchersStatus, setFetchVouchersStatus] = useState<'loading' | 'success' | 'failed'>(
    'loading',
  )
  const [voucherStatus, setVoucherStatus] = useState<VoucherStatusEnum>(VoucherStatusEnum.ACTIVE)
  const [totalRowVouchers, setTotalRowVouchers] = useState(0)
  const [pageVouchers, setPageVouchers] = useState(1)
  const [limitVouchers, setLimitVouchers] = useState(10)
  const [keywordSearchVoucher, setKeywordSearchVoucher] = useState('')
  const [selectedTagsVoucher, setSelectedTagsVoucher] = useState<IFilter[]>([])
  const [vouchers, setVouchers] = useState<AdminsLoyaltyGetVoucherListResponseItem[]>([])
  const [isOpenModalVoucherTags, setIsOpenModalVoucherTags] = useState(false)

  const voucherType: VoucherTypeEnum[] = [VoucherTypeEnum.Public, VoucherTypeEnum.Private]
  const userType: UserRoleEnum[] = [
    UserRoleEnum.Agent,
    UserRoleEnum.Harvester,
    UserRoleEnum.Driver,
    UserRoleEnum.FieldCoordinator,
  ]
  const paymentMethodType: PaymentMethodEnum[] = [
    PaymentMethodEnum.Mandiriva,
    PaymentMethodEnum.Briva,
  ]

  const [isOpenModalVoucherCancel, setIsOpenModalVoucherCancel] = useState(false)
  const [selectedVoucher, setSelectedVoucher] = useState<AdminsLoyaltyGetVoucherListResponseItem>(
    null as unknown as AdminsLoyaltyGetVoucherListResponseItem,
  )
  const [fetchCancelVoucherStatus, setFetchCancelVoucherStatus] = useState<
    'loading' | 'idle' | 'failed'
  >('idle')

  useEffect(() => {
    fetchVoucerList()
  }, [pageVouchers, limitVouchers, voucherStatus, selectedTagsVoucher])

  useEffect(() => {
    if (!isOpenModalVoucherCancel) {
      setSelectedVoucher(null as unknown as AdminsLoyaltyGetVoucherListResponseItem)
      setFetchCancelVoucherStatus('idle')
    }
  }, [isOpenModalVoucherCancel])

  const fetchVoucerList = useCallback(async () => {
    try {
      setFetchVouchersStatus('loading')
      const typeRequest = getFilterRequest(voucherType)
      const userRequest = getFilterRequest(userType)
      const paymentMethodRequest = getFilterRequest(paymentMethodType)

      const { data } = await adminsApi.adminsLoyaltyGetVoucherList(
        voucherStatus,
        typeRequest ? (typeRequest as VoucherTypeEnum) : undefined,
        userRequest ? (userRequest as UserRoleEnum) : undefined,
        paymentMethodRequest ? (paymentMethodRequest as PaymentMethodEnum) : undefined,
        'name',
        keywordSearchVoucher ? keywordSearchVoucher : undefined,
        pageVouchers,
        limitVouchers,
      )
      setFetchVouchersStatus('success')
      setVouchers(data.items)
      setTotalRowVouchers(data.pagination.total)
    } catch {
      setFetchVouchersStatus('failed')
    }
  }, [
    voucherStatus,
    voucherType,
    userType,
    paymentMethodType,
    keywordSearchVoucher,
    setFetchVouchersStatus,
    setVouchers,
    setTotalRowVouchers,
    adminsApi,
  ])

  const cancelVoucher = async () => {
    try {
      setFetchCancelVoucherStatus('loading')
      await adminsApi.adminsLoyaltyCancelVoucher(selectedVoucher.id)
      await toast('Berhasil menghapus voucher', 'success')
      setIsOpenModalVoucherCancel(false)
      setFetchCancelVoucherStatus('idle')
      await fetchVoucerList()
    } catch (error) {
      await toast('Gagal menghapus voucher', 'danger')
      setFetchCancelVoucherStatus('failed')
    }
  }

  const getFilterRequest = (list: (VoucherTypeEnum | UserRoleEnum | PaymentMethodEnum)[]) => {
    return list.filter(value => selectedTagsVoucher.some(item => item.value === value)).join(',')
  }

  const handleChangeVoucherStatus = (status: VoucherStatusEnum) => {
    setVoucherStatus(status)
    setPageVouchers(1)
  }

  const handleSearchVoucher = async () => {
    pageVouchers === 1 ? await fetchVoucerList() : setPageVouchers(1)
  }

  const handleChangeCurrentLimit = (limit: number) => {
    setLimitVouchers(limit)
    setPageVouchers(1)
  }

  const handleChangeCurrentPage = (page: number) => {
    setPageVouchers(page)
  }

  const handleSelectTag = async (tag: IFilter) => {
    if (selectedTagsVoucher.some(item => item.value === tag.value)) {
      setSelectedTagsVoucher(selectedTagsVoucher.filter(item => item.value !== tag.value))
    } else {
      setSelectedTagsVoucher([...selectedTagsVoucher, tag])
    }
    setPageVouchers(1)
  }

  const handleSelectAdvancedFilter = async (selectedTags: IFilter[]) => {
    setSelectedTagsVoucher(selectedTags)
    setIsOpenModalVoucherTags(false)
    setPageVouchers(1)
  }

  const handleOpenModalVoucherCancel = (voucher: AdminsLoyaltyGetVoucherListResponseItem) => {
    setIsOpenModalVoucherCancel(true)
    setSelectedVoucher(voucher)
  }

  return {
    toast,
    cancelVoucher,
    fetchCancelVoucherStatus,
    fetchVoucerList,
    fetchVouchersStatus,
    handleChangeCurrentLimit,
    handleChangeCurrentPage,
    handleChangeVoucherStatus,
    handleOpenModalVoucherCancel,
    handleSearchVoucher,
    handleSelectAdvancedFilter,
    handleSelectTag,
    isOpenModalVoucherCancel,
    isOpenModalVoucherTags,
    keywordSearchVoucher,
    limitVouchers,
    pageVouchers,
    selectedTagsVoucher,
    selectedVoucher,
    setIsOpenModalVoucherCancel,
    setIsOpenModalVoucherTags,
    setKeywordSearchVoucher,
    toastContainer,
    totalRowVouchers,
    vouchers,
    voucherStatus,
  }
}
